import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { listInstanceUsers, listInternalUsers } from '../../../api/instances';
import { buildCommandCenterPath, buildPathWithSlug, getLoginUniqueId } from '../../../helpers/commandCentreRouting';
import { resolveProfilePictureUrl } from '../utils/profilePicture';
import './CommandCenterHeader.css';

const CommandCenterHeader: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const loginUniqueId = getLoginUniqueId(location.pathname, location.search);
  const ticketsPath = buildCommandCenterPath(loginUniqueId, location.search);
  const coreNetworkPath = buildPathWithSlug('/command-center/mvp-talent', loginUniqueId, location.search);
  const systemPath = buildPathWithSlug('/system', loginUniqueId, location.search);
  const growthPath = buildPathWithSlug('/growth/accounts', loginUniqueId, location.search);
  const revenuePath = buildPathWithSlug('/revenue', loginUniqueId, location.search);
  const ezeeAiPath = buildPathWithSlug('/ezee-ai', loginUniqueId, location.search);
  const canSeeCorporateGrowth = user?.commandCentreAccess?.scope === 'global';

  const displayName = useMemo(() => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return name || user?.email || 'Command Centre User';
  }, [user]);

  const initials = useMemo(() => {
    const parts = displayName.split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?';
  }, [displayName]);

  useEffect(() => {
    let cancelled = false;

    const loadProfilePicture = async () => {
      if (!user?.id) {
        setProfilePicture(null);
        return;
      }

      try {
        const users = user.instanceID
          ? (await listInstanceUsers(Number(user.instanceID))).users
          : await listInternalUsers();
        const matched = users.find((candidate) => Number(candidate.id) === Number(user.id));
        if (!cancelled) {
          setProfilePicture(
            resolveProfilePictureUrl(matched?.profilePicture ?? matched?.profile_picture) || null
          );
        }
      } catch {
        if (!cancelled) setProfilePicture(null);
      }
    };

    loadProfilePicture();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.instanceID]);

  return (
    <header className="command-center-global-header">
      <div className="command-center-global-header__inner">
        <NavLink
          to={ticketsPath}
          end
          className="command-center-global-header__logo-link"
          aria-label="Elite Core Command Center home"
        >
          <img src="/logo.png" alt="Elite Core" className="command-center-global-header__logo" />
        </NavLink>

        <nav className="command-center-global-header__nav" aria-label="Command Center primary navigation">
          <NavLink to={ticketsPath} end className="command-center-global-header__link">
            Tickets
          </NavLink>
          <NavLink to={coreNetworkPath} className="command-center-global-header__link">
            Core Network
          </NavLink>
          <NavLink to={systemPath} className="command-center-global-header__link">
            System
          </NavLink>
          {canSeeCorporateGrowth ? (
            <>
              <NavLink to={growthPath} className="command-center-global-header__link">
                Growth
              </NavLink>
              <NavLink to={revenuePath} className="command-center-global-header__link">
                Revenue
              </NavLink>
            </>
          ) : null}
          <NavLink to={ezeeAiPath} className="command-center-global-header__link">
            Ezee AI
          </NavLink>
        </nav>

        <div
          className="command-center-global-header__account"
          onMouseEnter={() => setShowAccountMenu(true)}
          onMouseLeave={() => setShowAccountMenu(false)}
        >
          <button
            type="button"
            className="command-center-global-header__account-button"
            aria-label={`Account menu for ${displayName}`}
            aria-expanded={showAccountMenu}
            onClick={() => setShowAccountMenu((open) => !open)}
          >
            {profilePicture ? (
              <img
                src={profilePicture}
                alt=""
                className="command-center-global-header__avatar"
              />
            ) : (
              <span className="command-center-global-header__avatar command-center-global-header__avatar--fallback">
                {initials}
              </span>
            )}
            <span className="command-center-global-header__account-name">{displayName}</span>
          </button>

          {showAccountMenu ? (
            <div className="command-center-global-header__account-menu">
              <div className="command-center-global-header__account-email">{user?.email}</div>
              <button type="button" onClick={logout}>
                <i className="fa fa-sign-out-alt" aria-hidden="true" /> Logout
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default CommandCenterHeader;
